/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {useEffect} from 'react';

import {ValueOf} from 'shared/web';

import {AlertVariants, AlertDefaultProps, AlertDetailedProps} from '../../Alert/Alert';
import type {
  NotificationConfigBase,
  NotificationProps,
  NotificationsContextProps,
} from '../Notification.types';

type NotificationConfig =
  | (NotificationConfigBase & Omit<AlertDefaultProps, 'onClose' | 'message' | 'variant'>)
  | (NotificationConfigBase & Omit<AlertDetailedProps, 'onClose' | 'message' | 'variant'>);

export const NOTIFICATIONS_EVENTS = {
  show: 'show-notification',
  hide: 'hide-notification',
  update: 'update-notification',
  clean: 'clean-notifications',
  cleanQueue: 'clean-notifications-queue',
} as const;

type NOTIFICATIONS_EVENTS_KEYS = keyof typeof NOTIFICATIONS_EVENTS;

// eslint-disable-next-line typescript/no-explicit-any -- Pre-existing any
export function createEvent(type: ValueOf<typeof NOTIFICATIONS_EVENTS>, detail?: any) {
  return new CustomEvent(type, {detail});
}

const prepareShowNotification =
  (variant: AlertVariants) => (message?: string | string[], config?: NotificationConfig) => {
    window.dispatchEvent(
      createEvent(NOTIFICATIONS_EVENTS.show, {
        ...config,
        message,
        variant,
      })
    );
  };

export const showNotification = {
  success: prepareShowNotification('success'),
  info: prepareShowNotification('info'),
  warning: prepareShowNotification('warning'),
  error: prepareShowNotification('error'),
} as const satisfies {[K in AlertVariants]: ReturnType<typeof prepareShowNotification>};

export function updateNotification(notification: Partial<NotificationProps> & {id: string}) {
  window.dispatchEvent(createEvent(NOTIFICATIONS_EVENTS.update, notification));
}

export function hideNotification(id: string) {
  window.dispatchEvent(createEvent(NOTIFICATIONS_EVENTS.hide, id));
}

export function cleanNotifications() {
  window.dispatchEvent(createEvent(NOTIFICATIONS_EVENTS.clean));
}

export function cleanNotificationsQueue() {
  window.dispatchEvent(createEvent(NOTIFICATIONS_EVENTS.cleanQueue));
}

export function useNotificationsEvents(ctx: NotificationsContextProps) {
  // eslint-disable-next-line typescript/no-explicit-any -- Pre-existing any
  const events: Record<NOTIFICATIONS_EVENTS_KEYS, any> = {
    show: (event: any) => ctx.showNotification(event.detail), // eslint-disable-line typescript/no-explicit-any -- Pre-existing any
    hide: (event: any) => ctx.hideNotification(event.detail), // eslint-disable-line typescript/no-explicit-any -- Pre-existing any
    update: (event: any) => ctx.updateNotification(event.detail.id, event.detail), // eslint-disable-line typescript/no-explicit-any -- Pre-existing any
    clean: ctx.clean,
    cleanQueue: ctx.cleanQueue,
  };

  useEffect(function subscribeToNotificationEvents() {
    Object.keys(events).forEach((event) => {
      window.addEventListener(
        NOTIFICATIONS_EVENTS[event as NOTIFICATIONS_EVENTS_KEYS],
        events[event as NOTIFICATIONS_EVENTS_KEYS]
      );
    });

    return () => {
      Object.keys(events).forEach((event) => {
        window.removeEventListener(
          NOTIFICATIONS_EVENTS[event as NOTIFICATIONS_EVENTS_KEYS],
          events[event as NOTIFICATIONS_EVENTS_KEYS]
        );
      });
    };
  }, []); // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
}
